import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Target,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateServiceCategoryModal from '../../components/admin/CreateServiceCategoryModal';
import { useToastContext } from "../../contexts/ToastContext";

import { 
  useAdminServiceCategories, 
  useCreateServiceCategory, 
  useUpdateServiceCategory, 
  useDeleteServiceCategory, 
  useToggleCategoryStatus,
  SERVICE_TYPES 
} from '../../services/serviceCategoryService';
const AdminServiceCategories = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
//   const { showToast } = useToastContext();
  const { success, error: showError } = useToastContext();
  // API query parameters
  const queryParams = {
    active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    service_type: typeFilter === 'all' ? undefined : typeFilter,
    sort_by: 'sort_order',
    sort_order: 'asc'
  };

  // Fetch categories from API
  const { 
    data: categoriesData, 
    isLoading: loading, 
    error: categoriesError,
    refetch 
  } = useAdminServiceCategories(queryParams);

  // Extract categories from API response
  const categories = categoriesData?.data?.data || [];

  // API mutations
  const createCategoryMutation = useCreateServiceCategory({
    onSuccess: () => {
        success('Service category created successfully', 'success');
      setShowCreateModal(false);
    },
    onError: (error) => {
        showError(error.message || 'Failed to create service category', 'error');
    }
  });

  const updateCategoryMutation = useUpdateServiceCategory({
    onSuccess: () => {
        success('Service category updated successfully', 'success');
      setShowEditModal(false);
      setSelectedCategory(null);
    },
    onError: (error) => {
        showError(error.message || 'Failed to update service category', 'error');
    }
  });

  const deleteCategoryMutation = useDeleteServiceCategory({
    onSuccess: () => {
        success('Service category deleted successfully', 'success');
    },
    onError: (error) => {
        showError(error.message || 'Failed to delete service category', 'error');
    }
  });

  const toggleStatusMutation = useToggleCategoryStatus({
    onSuccess: () => {
    //   showToast('Category status updated successfully', 'success');
      success('Category status updated successfully', 'success');;
    },
    onError: (error) => {
        showError(error.message || 'Failed to update category status', 'error');
    }
  });

  // Client-side filtering for search (API handles status and type filtering)
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.category_description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Define columns for DataTable
  const columns = [
    {
      key: 'category',
      label: 'Category',
      render: (category) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
              {category.emoji ? (
                <span className="text-lg">{category.emoji}</span>
              ) : (
                <Target className="h-5 w-5 text-white" />
              )}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {category.category_name}
            </div>
            <div className="text-sm text-gray-500">
              {category.category_description}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'emoji',
      label: 'Emoji',
      render: (category) => (
        <div className="text-center">
          {category.emoji ? (
            <span className="text-2xl">{category.emoji}</span>
          ) : (
            <span className="text-gray-400 text-sm">No emoji</span>
          )}
        </div>
      )
    },
    {
      key: 'service_type',
      label: 'Type',
      render: (category) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getServiceTypeLabel(category.service_type)}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (category) => (
        <div className="flex items-center">
          {getStatusIcon(category.is_active)}
          <span className="ml-2">
            {getStatusBadge(category.is_active)}
          </span>
        </div>
      )
    },
    {
      key: 'sort_order',
      label: 'Sort Order',
      render: (category) => (
        <span className="text-sm text-gray-900">{category.sort_order}</span>
      )
    }
  ];

  // Define actions for DataTable
  const actions = [
    {
      label: 'Toggle',
      icon: CheckCircle,
      onClick: (category) => handleToggleStatus(category.id),
      title: 'Toggle Status',
      variant: 'default'
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: (category) => openEditModal(category),
      title: 'Edit Category',
      variant: 'default'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (category) => handleDeleteCategory(category.id),
      title: 'Delete Category',
      variant: 'default'
    }
  ];

  const handleCreateCategory = (categoryData) => {
    createCategoryMutation.mutate(categoryData);
  };

  const handleEditCategory = (categoryData) => {
    updateCategoryMutation.mutate({
      id: selectedCategory.id,
      data: categoryData
    });
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('Are you sure you want to delete this service category?')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  const handleToggleStatus = (categoryId) => {
    toggleStatusMutation.mutate(categoryId);
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const getStatusIcon = (isActive) => {
    return isActive ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Inactive
      </span>
    );
  };

  const getServiceTypeLabel = (type) => {
    const serviceType = SERVICE_TYPES.find(st => st.value === type);
    return serviceType ? serviceType.label : type;
  };

  // Handle API errors
  if (categoriesError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Categories</h3>
          <p className="mt-1 text-sm text-gray-500">
            {categoriesError.message || 'Failed to load service categories'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading service categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Categories</h1>
            <p className="mt-2 text-gray-600">Manage service categories for the platform</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>

        {/* Filters */}
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <div className="w-full sm:w-52">
              <SearchSelect
              label="Status Filter"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Filter by status"
              />
              </div>

              <div className="w-full sm:w-52">
              <SearchSelect
              label="Type Filter"
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: 'all', label: 'All Types' },
                  ...SERVICE_TYPES
                ]}
                placeholder="Filter by type"
              />
              </div>
            </div>

            
          </div>
        </div>

        {/* Categories Table */}
        <DataTable
          data={filteredCategories}
          columns={columns}
          actions={actions}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search categories..."
          emptyState={{
            icon: AlertCircle,
            title: "No categories found",
            description: searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating a new service category.',
            action: (!searchTerm && statusFilter === 'all' && typeFilter === 'all') ? (
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
            ) : null
          }}
        />

        {/* Create Modal */}
        {showCreateModal && (
          <CreateServiceCategoryModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateCategory}
            serviceTypes={SERVICE_TYPES}
            isLoading={createCategoryMutation.isLoading}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedCategory && (
          <CreateServiceCategoryModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedCategory(null);
            }}
            onSave={handleEditCategory}
            serviceTypes={SERVICE_TYPES}
            category={selectedCategory}
            isEdit={true}
            isLoading={updateCategoryMutation.isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AdminServiceCategories;
